import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useCheckoutSession } from '../../hooks/useCheckoutSession';
import { tempeFabricationClientWrapper } from '../../services/tempeFabricationClientWrapper';

interface CartSlideoverProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartSlideover: React.FC<CartSlideoverProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { items, getTotalPrice, incrementQuantity, decrementQuantity, removeItem, clearCart, getCartHash } = useCart();
  const { initializeCheckout, clearSession } = useCheckoutSession();

  const calculateItemPrice = (price: number, discount?: number) => {
    if (!discount) return price;
    return price - (price * discount / 100);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            transition: 'opacity 0.3s ease'
          }}
          onClick={onClose}
        />
      )}

      {/* Slideover */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'white',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e5e5'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: items.length > 0 ? '12px' : '0'
            }}
          >
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
              Shopping Cart ({items.length})
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                lineHeight: '1'
              }}
              aria-label="Close cart"
            >
              ×
            </button>
          </div>
          {items.length > 0 && (
            <button
              onClick={async () => {
                if (window.confirm('Are you sure you want to clear your entire cart?')) {
                  console.log('[CartSlideover] Clearing cart and checkout session');

                  // Read session fresh from localStorage to avoid stale state
                  const CHECKOUT_SESSION_KEY = 'tempefab_checkout_session';
                  const storedSession = localStorage.getItem(CHECKOUT_SESSION_KEY);
                  const currentSession = storedSession ? JSON.parse(storedSession) : null;

                  console.log('[CartSlideover] Current session from localStorage:', currentSession);
                  console.log('[CartSlideover] Session paymentIntentId:', currentSession?.paymentIntentId);
                  console.log('[CartSlideover] Session ttl:', currentSession?.ttl);

                  // Cancel payment intent if there's a valid session
                  if (currentSession?.paymentIntentId && currentSession.ttl) {
                    const now = Date.now();
                    const ttlMs = currentSession.ttl * 1000; // Convert TTL from seconds to milliseconds
                    const isExpired = now > ttlMs;

                    console.log('[CartSlideover] Session validation - now:', now, 'ttlMs:', ttlMs, 'isExpired:', isExpired);

                    if (!isExpired) {
                      console.log('[CartSlideover] Valid payment intent session found, cancelling payment intent:', currentSession.paymentIntentId);
                      try {
                        await tempeFabricationClientWrapper.cancelPaymentIntent(currentSession.paymentIntentId);
                        console.log('[CartSlideover] Payment intent cancelled successfully');
                      } catch (error) {
                        console.error('[CartSlideover] Error cancelling payment intent (non-critical):', error);
                      }
                    } else {
                      console.log('[CartSlideover] Payment intent session expired, skipping cancellation');
                    }
                  } else {
                    console.log('[CartSlideover] No valid session found - currentSession:', currentSession);
                  }

                  clearCart();
                  clearSession(); // Clear checkout session when cart is cleared
                }
              }}
              className="butn border rounded-pill text-danger"
              style={{
                fontSize: '14px',
                padding: '6px 16px',
                width: '100%'
              }}
            >
              <span>Clear Cart</span>
            </button>
          )}
        </div>

        {/* Cart Items */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 24px'
          }}
        >
          {items.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#999'
              }}
            >
              <i
                className="la la-shopping-cart"
                style={{ fontSize: '64px', marginBottom: '16px', display: 'block' }}
              />
              <p style={{ fontSize: '18px', margin: 0 }}>Your cart is empty</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {items.map((item) => {
                  const itemPrice = calculateItemPrice(item.price, item.discount);
                  const customizationCost = item.customization?.additionalCost || 0;
                  const priceWithCustomization = itemPrice + customizationCost;
                  const itemTotal = priceWithCustomization * item.quantity;

                  return (
                    <div
                      key={item.cartItemId}
                      style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '16px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px'
                      }}
                    >
                      {/* Product Image */}
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          flexShrink: 0,
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                      </div>

                      {/* Product Details */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {item.name}
                          </h3>
                          <button
                            onClick={() => removeItem(item.cartItemId)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#999',
                              cursor: 'pointer',
                              fontSize: '18px',
                              padding: '0',
                              lineHeight: '1'
                            }}
                            aria-label="Remove item"
                          >
                            ×
                          </button>
                        </div>

                        {/* Customization Info */}
                        {item.customization && (
                          <div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                            {item.customization.name}
                            {customizationCost > 0 && ` (+$${customizationCost.toFixed(2)})`}
                          </div>
                        )}

                        {/* Price */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(item.discount != null && item.discount > 0) && (
                            <span
                              style={{
                                textDecoration: 'line-through',
                                color: '#999',
                                fontSize: '14px'
                              }}
                            >
                              ${item.price.toFixed(2)}
                            </span>
                          )}
                          <span style={{ fontSize: '16px', fontWeight: '700', color: '#333' }}>
                            ${priceWithCustomization.toFixed(2)}
                          </span>
                        </div>

                        {/* Quantity Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={() => decrementQuantity(item.cartItemId)}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              border: '2px solid #ff5722',
                              backgroundColor: 'white',
                              color: '#ff5722',
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            −
                          </button>
                          <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '30px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => incrementQuantity(item.cartItemId)}
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              border: '2px solid #ff5722',
                              backgroundColor: 'white',
                              color: '#ff5722',
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                          >
                            +
                          </button>
                          <span style={{ marginLeft: 'auto', fontSize: '16px', fontWeight: '700' }}>
                            ${itemTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total and Checkout - Right after items list */}
              <div
                style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>Total:</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#ff5722' }}>
                    ${getTotalPrice().toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    console.log('[CartSlideover] Checkout button clicked');
                    // Initialize checkout session with cart hash
                    const cartHash = getCartHash();
                    console.log('[CartSlideover] Generated cart hash:', cartHash);
                    const clientId = initializeCheckout(cartHash);
                    console.log('[CartSlideover] Initialized checkout with clientId:', clientId);

                    onClose();
                    console.log('[CartSlideover] Navigating to /checkout');
                    navigate('/checkout');
                  }}
                  style={{
                    width: '100%',
                    backgroundColor: '#ff5722',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50px',
                    padding: '16px 32px',
                    fontSize: '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e64a19')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#ff5722')}
                >
                  Checkout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSlideover;
